import React, { useState, useEffect } from "react";
import { submitAnswer } from "api/Contact";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { toast } from "react-toastify";
import { useUserData } from "context/OAuth/AuthContext";
import { useTranslation } from "react-i18next";

const ContactUsForm = () => {
  const { user } = useUserData();
  const [formData, setFormData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    subject: "General Inquiry",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useTranslation();
  // Update form data when user data loads
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
      }));
    }
  }, [user]);

  // Helper function to validate a specific field
  const validateField = (name, value) => {
    let error = "";

    if (!value.trim()) {
      switch (name) {
        case "first_name":
          error = t("First name is required.");
          break;
        case "last_name":
          error = t("Last name is required.");
          break;
        case "message":
          error = t("Please enter a message.");
          break;
        case "subject":
          error = t("A subject must be selected.");
          break;
        default:
          break;
      }
    } else if (name === "message" && value.length < 10) {
      error = t("Message must be at least 10 characters long.");
    }

    // Only update if error has changed
    setValidationErrors((prevErrors) => {
      if (prevErrors[name] === error) return prevErrors;
      return {
        ...prevErrors,
        [name]: error,
      };
    });

    return error.length === 0;
  };

  // Function to validate the entire form
  const validateForm = () => {
    const fieldsToValidate = ["first_name", "last_name", "subject", "message"];
    let isFormValid = true;

    fieldsToValidate.forEach((name) => {
      const isValid = validateField(name, formData[name]);
      if (!isValid) {
        isFormValid = false;
      }
    });

    return isFormValid;
  };

  // Handle input change with auto-validation
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({ ...prevData, [name]: value }));

    // Auto-validate when user is typing (with debouncing logic for better UX)
    if (validationErrors[name]) {
      // Clear the error immediately if the field now has value
      if (value.trim()) {
        if (name === "message") {
          // For message, also check length
          if (value.length >= 10) {
            setValidationErrors((prev) => ({
              ...prev,
              [name]: "",
            }));
          } else {
            // Re-validate to show updated error message
            validateField(name, value);
          }
        } else {
          // For other fields, just clear if not empty
          setValidationErrors((prev) => ({
            ...prev,
            [name]: "",
          }));
        }
      }
    }

    // If form was already submitted, validate on every change
    if (isSubmitted) {
      validateField(name, value);
    }
  };

  // Handle blur with validation
  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  // Handle radio change with immediate validation
  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    // Clear subject error immediately when a selection is made
    if (validationErrors.subject) {
      setValidationErrors((prev) => ({
        ...prev,
        subject: "",
      }));
    }

    if (isSubmitted) {
      validateField(name, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsSubmitted(true);

    // const isValid = validateForm();

    // if (!isValid) {
    //   toast.error("Please correct the errors in the form before submitting.");
    //   setLoading(false);
    //   return;
    // }

    try {
      await submitAnswer(formData);

      // Reset form
      setFormData({
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        subject: "General Inquiry",
        message: "",
      });
      setValidationErrors({});
      setIsSubmitted(false);

      toast.info(t("toast.informationSubmit"));
    } catch (e) {
      console.log("Error in submitting form", e);
      toast.error(t("toast.failedSubmit"));
    } finally {
      setLoading(false);
    }
  };

  // Check if form is valid
  const isFormValid =
    formData.first_name.trim() !== "" &&
    formData.last_name.trim() !== "" &&
    formData.message.trim().length >= 10 &&
    formData.subject.trim() !== "";

  // Update the RadioOption component to use the new handler
  const RadioOption = ({ id, label, value, checked, onChange }) => (
    <div className="flex items-center space-x-2">
      <input
        type="radio"
        id={id}
        name="subject"
        value={value}
        checked={checked}
        onChange={handleRadioChange} // Use the new handler
        className="form-radio h-5 w-5 text-[#15AAFC] border-gray-300 focus:ring-0 cursor-pointer"
        style={{
          appearance: "none",
          borderRadius: "50%",
          border: checked ? "5px solid #0771EF" : "2px solid #D1D5DB",
          outline: "none",
          transition: "all 0.1s ease",
        }}
      />
      <label
        htmlFor={id}
        className="text-[#1B1F26] font-[Lato] text-[16px] cursor-pointer"
      >
        {label}
      </label>
    </div>
  );

  return (
    <div className="flex justify-center items-center p-0 sm:p-4 w-full">
      <div
        className="p-1 w-full max-w-lg"
        style={{
          background: "linear-gradient(to bottom right, #0771EF, #15AAFC)",
          borderRadius: "20px",
        }}
      >
        <div className="bg-white rounded-[17px] p-6 sm:p-8 md:p-10 w-full shadow-xl">
          {/* --- Header Section --- */}
          <h1
            style={{
              background:
                "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
            className="font-[800] font-[Lato] text-[24px] sm:text-[28px] text-center uppercase tracking-wider"
          >
            {t("Contact Us")}
          </h1>
          <p className="text-[#1B1F26B8] font-[400] font-[Lato] text-[15px] sm:text-[16px] text-center mb-6">
            {t("Any question or remarks? Just write us a message!")}
          </p>
          {/* --- Form Starts Here --- */}
          <form onSubmit={handleSubmit}>
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* First Name */}
              <div>
                <label className="block text-[#1B1F26] font-[Lato] text-sm mb-1">
                  {t("First name")}
                </label>
                <input
                  type="text"
                  name="first_name"
                  placeholder={t("First name")}
                  value={formData.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full p-3 border rounded-lg focus:ring-[#15AAFC] focus:border-[#15AAFC] 
                             ${
                               validationErrors.first_name
                                 ? "border-red-500"
                                 : "border-gray-300"
                             }`}
                />
                {validationErrors.first_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.first_name}
                  </p>
                )}
              </div>
              {/* Last Name */}
              <div>
                <label className="block text-[#1B1F26] font-[Lato] text-sm mb-1">
                  {t("Last name")}
                </label>
                <input
                  type="text"
                  name="last_name"
                  placeholder={t("Last name")}
                  value={formData.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full p-3 border rounded-lg focus:ring-[#15AAFC] focus:border-[#15AAFC] 
                             ${
                               validationErrors.last_name
                                 ? "border-red-500"
                                 : "border-gray-300"
                             }`}
                />
                {validationErrors.last_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.last_name}
                  </p>
                )}
              </div>
            </div>

            {/* Subject Radios */}
            <div className="mb-6">
              <p className="block text-[#1B1F26] font-[Lato] text-sm mb-2">
                {t("Select Subject")}
              </p>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-y-3 gap-x-4">
                <RadioOption
                  id="general"
                  label={t("General Inquiry")}
                  value="General Inquiry"
                  checked={formData.subject === "General Inquiry"}
                  onChange={handleChange}
                />
                <RadioOption
                  id="subscription"
                  label={t("Subscription")}
                  value="Subscription"
                  checked={formData.subject === "Subscription"}
                  onChange={handleChange}
                />
                <RadioOption
                  id="diet"
                  label={t("Diet Plan")}
                  value="Diet Plan"
                  checked={formData.subject === "Diet Plan"}
                  onChange={handleChange}
                />
                <RadioOption
                  id="exercise"
                  label={t("Exercise Plan")}
                  value="Exercise Plan"
                  checked={formData.subject === "Exercise Plan"}
                  onChange={handleChange}
                />
              </div>
              {validationErrors.subject && (
                <p className="text-red-500 text-xs mt-3">
                  {validationErrors.subject}
                </p>
              )}
            </div>

            {/* Message Textarea */}
            <div className="mb-8">
              <label className="block text-[#1B1F26] font-[Lato] text-sm mb-1">
                {t("Message")}
              </label>
              <textarea
                name="message"
                placeholder={t("Write the message...")}
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                rows="4"
                className={`w-full p-3 border rounded-lg resize-none focus:ring-[#15AAFC] focus:border-[#15AAFC] 
                             ${
                               validationErrors.message
                                 ? "border-red-500"
                                 : "border-gray-300"
                             }`}
              ></textarea>
              {validationErrors.message && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`w-full py-4 text-white font-[Lato] text-lg rounded-xl transition duration-300 min-h-[60px]
                ${
                  loading || !isFormValid
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:shadow-lg"
                }
                `}
              style={{
                background:
                  "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)",
              }}
              disabled={loading || !isFormValid}
            >
              {loading ? <ButtonLoader /> : t("Send a Message")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactUsForm;
